/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useBlocker } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

/**
 * Warn before leaving an editor with unpublished changes.
 *
 * Two ways out, both covered: a link inside Vellum asks with the browser's own
 * confirm, and closing or reloading the tab asks through `beforeunload`, whose
 * words the browser chooses. Moving between an editor's own steps is not
 * leaving -- those change only the search, so they pass when `sameScreen`.
 *
 * `release` lets Publish and Cancel go on without asking: once one of them
 * has done its work there is nothing left to warn about.
 */
export function useLeaveGuard({
  when,
  message,
  sameScreen = false,
}: {
  when: boolean;
  message: string;
  /** Let a move to the same path through, as a step change is. */
  sameScreen?: boolean;
}): { release: () => void } {
  const released = useRef(false);
  const live = useRef(when);
  useEffect(() => {
    live.current = when;
  }, [when]);

  useBlocker({
    shouldBlockFn: ({ current, next }) => {
      if (released.current || !live.current) return false;
      if (sameScreen && current.pathname === next.pathname) return false;
      return !window.confirm(message);
    },
    enableBeforeUnload: () => !released.current && live.current,
  });

  return {
    release: () => {
      released.current = true;
    },
  };
}
